import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  DataTable,
  Group,
  InlineError,
  Input,
  Modal,
  PageHeader,
  PageRouteScaffold,
  Stack,
  Switch,
  Text,
  openConfirmDelete,
} from '@wapharma/design-system';
import { apiDelete, apiPatch, apiPost } from '../api';
import { apiGetListSimple } from '../apiList';
import { getDisplayError } from '../apiErrors';
import { getToken } from '../auth';
import { useToast } from '../contexts/ToastContext';

type DiscountRow = {
  id: string;
  code: string;
  value: number;
  startsAt: string | null;
  endsAt: string | null;
  usageLimit: number | null;
  perEmailLimit: number | null;
  channelScope: string | null;
  isActive: boolean;
  _count?: { redemptions: number };
};

export function DiscountCodesPage({
  embedded = false,
  onChanged,
}: {
  embedded?: boolean;
  onChanged?: () => void;
} = {}) {
  const token = getToken();
  const { showToast } = useToast();
  const [rows, setRows] = useState<DiscountRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState<DiscountRow | null>(null);
  const [code, setCode] = useState('');
  const [value, setValue] = useState('10');
  const [usageLimit, setUsageLimit] = useState('');
  const [perEmailLimit, setPerEmailLimit] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [channelScope, setChannelScope] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [pending, setPending] = useState(false);

  const load = useCallback(() => {
    if (!token) return;
    setLoading(true);
    void apiGetListSimple<DiscountRow>('/sales/discount-codes', token)
      .then(setRows)
      .catch((e) => setError(getDisplayError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setCode('');
    setValue('10');
    setUsageLimit('');
    setPerEmailLimit('');
    setStartsAt('');
    setEndsAt('');
    setChannelScope('');
    setIsActive(true);
  };

  const openEdit = (row: DiscountRow) => {
    setEdit(row);
    setValue(String(row.value));
    setUsageLimit(row.usageLimit != null ? String(row.usageLimit) : '');
    setPerEmailLimit(row.perEmailLimit != null ? String(row.perEmailLimit) : '');
    setStartsAt(row.startsAt ? row.startsAt.slice(0, 16) : '');
    setEndsAt(row.endsAt ? row.endsAt.slice(0, 16) : '');
    setChannelScope(row.channelScope ?? '');
    setIsActive(row.isActive);
  };

  const create = () => {
    if (!token || pending) return;
    setPending(true);
    void apiPost(
      '/sales/discount-codes',
      {
        code: code.trim(),
        value: Number(value),
        ...(usageLimit.trim() ? { usageLimit: Number(usageLimit) } : {}),
        ...(perEmailLimit.trim() ? { perEmailLimit: Number(perEmailLimit) } : {}),
        ...(startsAt.trim() ? { startsAt: new Date(startsAt).toISOString() } : {}),
        ...(endsAt.trim() ? { endsAt: new Date(endsAt).toISOString() } : {}),
        ...(channelScope.trim() ? { channelScope: channelScope.trim() } : {}),
        isActive,
      },
      token,
    )
      .then(() => {
        setCreateOpen(false);
        resetForm();
        load();
        onChanged?.();
        showToast('Code promo créé.', { tone: 'success' });
      })
      .catch((e) => showToast(getDisplayError(e), { tone: 'error' }))
      .finally(() => setPending(false));
  };

  const saveEdit = () => {
    if (!token || !edit || pending) return;
    setPending(true);
    void apiPatch(
      `/sales/discount-codes/${edit.id}`,
      {
        value: Number(value),
        usageLimit: usageLimit.trim() ? Number(usageLimit) : null,
        perEmailLimit: perEmailLimit.trim() ? Number(perEmailLimit) : null,
        startsAt: startsAt.trim() ? new Date(startsAt).toISOString() : null,
        endsAt: endsAt.trim() ? new Date(endsAt).toISOString() : null,
        channelScope: channelScope.trim() || null,
        isActive,
      },
      token,
    )
      .then(() => {
        setEdit(null);
        load();
        onChanged?.();
        showToast('Code promo mis à jour.', { tone: 'success' });
      })
      .catch((e) => showToast(getDisplayError(e), { tone: 'error' }))
      .finally(() => setPending(false));
  };

  const formFields = (
    <Stack gap="sm">
      {createOpen ? (
        <Input label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
      ) : null}
      <Input label="Réduction %" type="number" value={value} onChange={(e) => setValue(e.target.value)} />
      <Input label="Début" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
      <Input label="Fin" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
      <Input label="Limite totale" value={usageLimit} onChange={(e) => setUsageLimit(e.target.value)} />
      <Input label="Limite / e-mail" value={perEmailLimit} onChange={(e) => setPerEmailLimit(e.target.value)} />
      <Input
        label="Canal (ECOMMERCE, SHOWROOM ou vide)"
        value={channelScope}
        onChange={(e) => setChannelScope(e.target.value)}
      />
      <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Switch checked={isActive} onChange={(e) => setIsActive(e.currentTarget.checked)} />
        <Text size="sm">Actif</Text>
      </label>
    </Stack>
  );

  const table = (
    <>
      {error ? <InlineError>{error}</InlineError> : null}
      {embedded ? (
        <Group justify="flex-end" style={{ marginBottom: 'var(--wp-space-12)' }}>
          <Button type="button" variant="primary" onClick={() => { resetForm(); setCreateOpen(true); }}>
            Nouveau code
          </Button>
        </Group>
      ) : null}
      <DataTable<DiscountRow>
        variant="list"
        loading={loading}
        data={rows}
        rowKey={(r) => r.id}
        emptyMessage="Aucun code promo."
        columns={[
          { key: 'code', label: 'Code' },
          { key: 'value', label: '%', render: (r) => String(r.value) },
          {
            key: 'active',
            label: 'Actif',
            render: (r) => (r.isActive ? 'Oui' : 'Non'),
          },
          {
            key: 'redemptions',
            label: 'Utilisations',
            render: (r) => String(r._count?.redemptions ?? 0),
          },
          {
            key: 'window',
            label: 'Validité',
            render: (r) => {
              const start = r.startsAt ? new Date(r.startsAt).toLocaleDateString('fr-FR') : '—';
              const end = r.endsAt ? new Date(r.endsAt).toLocaleDateString('fr-FR') : '—';
              return `${start} → ${end}`;
            },
          },
        ]}
        rowActions={(r) => (
          <Group gap="xs">
            <Button type="button" size="xs" variant="ghost" onClick={() => openEdit(r)}>
              Modifier
            </Button>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() =>
                openConfirmDelete({
                  title: 'Supprimer le code',
                  message: `Supprimer ${r.code} ?`,
                  onConfirm: () => {
                    if (!token) return Promise.resolve();
                    return apiDelete(`/sales/discount-codes/${r.id}`, token).then(() => {
                      load();
                      onChanged?.();
                      showToast('Code supprimé.', { tone: 'success' });
                    });
                  },
                })
              }
            >
              Supprimer
            </Button>
          </Group>
        )}
      />

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Nouveau code promo">
        {formFields}
        <Group justify="flex-end" style={{ marginTop: 'var(--wp-space-12)' }}>
          <Button type="button" variant="primary" loading={pending} onClick={create} disabled={!code.trim()}>
            Créer
          </Button>
        </Group>
      </Modal>

      <Modal opened={Boolean(edit)} onClose={() => setEdit(null)} title={`Modifier ${edit?.code ?? ''}`}>
        {formFields}
        <Group justify="flex-end" style={{ marginTop: 'var(--wp-space-12)' }}>
          <Button type="button" variant="primary" loading={pending} onClick={saveEdit}>
            Enregistrer
          </Button>
        </Group>
      </Modal>
    </>
  );

  if (embedded) return table;

  return (
    <PageRouteScaffold
      band={
        <PageHeader
          title="Codes promo"
          breadcrumbs={[{ label: 'Accueil', href: '/' }, { label: 'Codes promo' }]}
          variant="dock"
          end={
            <Button type="button" variant="primary" onClick={() => { resetForm(); setCreateOpen(true); }}>
              Nouveau code
            </Button>
          }
        />
      }
    >
      {table}
    </PageRouteScaffold>
  );
}
